import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Globe, Search, CheckCircle2, XCircle, ArrowRight, Loader2, Sparkles,
  ShoppingCart, X, LogIn, Trash2,
} from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { API, useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/hooks/useCart";

// Persist a pending checkout intent across the login/2fa hop.
const INTENT_KEY = "zynthoro_wd_intent_v1";
const saveIntent = (i) => { try { sessionStorage.setItem(INTENT_KEY, JSON.stringify(i)); } catch { /* ignore */ } };
const readIntent = () => {
  try { const v = sessionStorage.getItem(INTENT_KEY); return v ? JSON.parse(v) : null; }
  catch { return null; }
};
const clearIntent = () => { try { sessionStorage.removeItem(INTENT_KEY); } catch { /* ignore */ } };

// Exclusive checkout deal — 50% off Kickstart lifetime plans, only
// surfaced inside the cart drawer AFTER a Site plan or a Domain has been
// added. Prices reflect the discounted amount that will be charged by
// Stripe.
const KICKSTART_DEALS = [
  {
    key: "k1",
    label: "Kickstart 1",
    original_eur: 79,
    price_eur: 39,
    features: [
      "AI Assistants · 50 credits total (no monthly reset)",
      "Planning & Time Tracking · Communication · Canva Studio",
    ],
  },
  {
    key: "k2",
    label: "Kickstart 2",
    original_eur: 149,
    price_eur: 75,
    features: [
      "Everything in K1 · 150 credits total (no monthly reset)",
      "Finance & Invoicing · Sales · AI photo/video suite",
    ],
  },
  {
    key: "k3",
    label: "Kickstart 3",
    original_eur: 199,
    price_eur: 99,
    features: [
      "Everything in K2 · 300 credits total (no monthly reset)",
      "Accounting & Operations · Project management · Marketing & Content",
    ],
  },
];

// 7-TLD popular shortcut rail shown above the search results. Not the
// full worldwide catalog — just the quick-jump chips.
const POPULAR_TLDS = ["com", "nl", "de", "uk", "us", "eu", "ai"];

/**
 * /website — Website & Domains landing page. Purely additive: does not
 * touch any existing pricing/tier surface. Two Stripe subscription
 * plans (Basis, Plus) + a domain search & order flow across 11
 * extensions. Checkout does not accept additional Stripe promotion codes.
 */
export default function WebsiteAndDomains() {
  const { user, loading: authLoading } = useAuth();
  const isLoggedIn = !!user && user !== false;
  const cart = useCart();
  const [plans, setPlans] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_LIMIT = 30;
  const [busyPlan, setBusyPlan] = useState(null);
  const [busyDomain, setBusyDomain] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const resumedRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Website & Domains — Zynthoro";
    (async () => {
      try {
        const [a, b] = await Promise.all([
          axios.get(`${API}/website-domains/plans`),
          axios.get(`${API}/website-domains/domain-catalog`),
        ]);
        setPlans(a.data.plans || []);
        setExtensions(b.data.extensions || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load catalogs.");
      }
    })();
  }, []);

  // React Router doesn't scroll to #hash targets after client-side nav.
  // On mount and hash change, jump to the matching element if present.
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash.replace(/^#/, "");
      if (!hash) return;
      // Wait a tick for the DOM (extension chips etc.) to be laid out.
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    };
    scrollToHash();
    window.addEventListener("hashchange", scrollToHash);
    return () => window.removeEventListener("hashchange", scrollToHash);
  }, []);

  const gotoLogin = (returnTo) => {
    // Client-side navigation — no full-page reload → no blank white frame
    // between the click and the login form rendering. The RESUME_KEY
    // sessionStorage flag is what carries the cart-checkout intent
    // through auth, so even if `?return=` is dropped/mangled downstream,
    // the resume flow still fires on the way back.
    navigate(`/login?return=${encodeURIComponent(returnTo)}`);
  };

  // --- Cart add handlers ------------------------------------------------
  const addPlanToCart = (t) => {
    const res = cart.add({
      type: "subscribe",
      plan: t.key,
      label: t.label || `Zynthoro Site ${t.short_label}`,
      short_label: t.short_label,
      price_eur: t.price_eur,
      billing: "monthly",
    });
    if (res.added) toast.success(`${t.short_label} added to cart.`);
    else toast.info(`${t.short_label} is already in your cart.`);
  };

  const addDomainToCart = (r) => {
    const res = cart.add({
      type: "domain",
      domain: r.domain,
      ext: r.ext,
      label: r.domain,
      price_eur: r.price_eur,
      billing: "yearly",
    });
    if (res.added) toast.success(`${r.domain} added to cart.`);
    else toast.info(`${r.domain} is already in your cart.`);
  };

  const addKickstartToCart = (deal) => {
    const res = cart.add({
      type: "kickstart",
      plan: deal.key,           // "k1" | "k2" | "k3"
      label: `${deal.label} · Checkout deal`,
      short_label: deal.label,
      price_eur: deal.price_eur,
      original_eur: deal.original_eur,
      billing: "onetime",
    });
    if (res.added) toast.success(`${deal.label} added to cart at 50% off.`);
    else toast.info(`${deal.label} is already in your cart.`);
  };

  const searchDomains = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setResults([]);
    setTotal(0);
    try {
      const { data } = await axios.post(
        `${API}/website-domains/domain/search`,
        { query: query.trim(), offset: 0, limit: PAGE_LIMIT },
      );
      setResults(data.results || []);
      setTotal(data.total || (data.results || []).length);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Search failed.");
    } finally { setSearching(false); }
  };

  const loadMoreDomains = async () => {
    if (loadingMore || !query.trim()) return;
    setLoadingMore(true);
    try {
      const { data } = await axios.post(
        `${API}/website-domains/domain/search`,
        { query: query.trim(), offset: results.length, limit: PAGE_LIMIT },
      );
      const next = data.results || [];
      // Guard against duplicates if a request races.
      setResults((prev) => {
        const seen = new Set(prev.map((r) => r.domain));
        return [...prev, ...next.filter((r) => !seen.has(r.domain))];
      });
      setTotal(data.total || total);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load more domains.");
    } finally { setLoadingMore(false); }
  };

  // --- Unified Stripe cart checkout ------------------------------------
  // ONE Stripe Checkout Session for the entire cart (subscription plan +
  // one-time kickstart + domain). Backend endpoint `/cart/checkout`
  // handles the mixed line-item construction; we just POST the cart.
  const RESUME_KEY = "zynthoro_wd_resume_flow";

  const checkoutNextItem = async () => {
    const items = cart.items;
    if (items.length === 0) {
      sessionStorage.removeItem(RESUME_KEY);
      navigate("/dashboard?purchase=success");
      return;
    }
    setCheckingOut(true);
    // Mark the individual plan/domain buttons as busy for UI feedback.
    items.forEach((it) => {
      if (it.type === "subscribe") setBusyPlan(it.plan);
      else if (it.type === "domain") setBusyDomain(it.domain);
    });
    try {
      const success = `${window.location.origin}/dashboard?purchase=success`;
      const cancel = `${window.location.origin}/website?checkout=cancelled`;
      const payload = {
        items: items.map((it) => ({
          type: it.type,
          plan: it.plan || null,
          domain: it.domain || null,
          ext: it.ext || null,
        })),
        success_url: success,
        cancel_url: cancel,
      };
      const { data } = await axios.post(
        `${API}/website-domains/cart/checkout`,
        payload,
      );
      if (data.checkout_url) {
        // Cart is fully consumed by this single session — clear it so
        // the drawer is empty on return, whether success or cancel.
        sessionStorage.removeItem(RESUME_KEY);
        cart.clear();
        window.location.href = data.checkout_url;
        return;
      }
      toast.error("Could not start checkout.");
      setCheckingOut(false);
      setBusyPlan(null);
      setBusyDomain(null);
    } catch (err) {
      const status = err?.response?.status;
      // Session actually expired mid-checkout → route to login client-side.
      // We DO NOT treat 403 as "please log in" because /api/website-domains/*
      // can return 403 for trial-gate reasons (TRIAL_LOCKED / TRIAL_EXPIRED)
      // even when the user is fully authenticated — that would create an
      // endless /login → /website → /login loop.
      if (status === 401) {
        sessionStorage.setItem(RESUME_KEY, "1");
        setCheckingOut(false);
        setBusyPlan(null);
        setBusyDomain(null);
        toast.info("Please log in to complete checkout.");
        navigate(`/login?return=${encodeURIComponent("/website?resume=1")}`);
        return;
      }
      // Any other error (403, 4xx, 5xx, network) — show the backend's
      // message and keep the drawer open. The user is still logged in.
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : (detail && typeof detail === "object" && typeof detail.message === "string")
              ? detail.message
              : "Checkout failed. Please try again.";
      toast.error(msg);
      setCheckingOut(false);
      setBusyPlan(null);
      setBusyDomain(null);
      // Ensure the drawer stays open so the user sees their cart is intact.
      setCartOpen(true);
    }
  };

  const startCheckout = () => {
    if (cart.items.length === 0) return;
    if (!isLoggedIn) {
      // Persist a flag so we auto-resume after login.
      sessionStorage.setItem(RESUME_KEY, "1");
      return gotoLogin("/website?resume=1");
    }
    // Keep the drawer open through the whole flow — never blank.
    setCartOpen(true);
    try {
      checkoutNextItem();
    } catch (err) {
      // Belt-and-braces: any sync throw in the handler must not leave
      // the user staring at nothing.
      console.error("[Zynthoro] startCheckout threw:", err);
      toast.error("Could not start checkout. Please try again.");
      setCheckingOut(false);
    }
  };

  // Auto-resume after login/2FA or after a Stripe success round-trip.
  //
  // Bulletproof rules (2026-08-25 blank-screen fix):
  //   1. If the user arrives with an active resume intent (URL param
  //      OR sessionStorage flag) AND is logged in AND the cart has
  //      items → OPEN THE CART DRAWER first so the items are always
  //      visible, THEN kick off Stripe checkout. If checkout fails,
  //      the drawer stays open with the cart intact — the user is
  //      never left staring at a blank screen.
  //   2. If the user arrives with an intent flag but no items in the
  //      cart, silently clear the flag (nothing to resume).
  useEffect(() => {
    if (authLoading) return;
    if (resumedRef.current) return;
    const url = new URL(window.location.href);
    const wantResume = url.searchParams.get("resume") === "1" || sessionStorage.getItem(RESUME_KEY) === "1";
    // Legacy single-item ?checkout=basis|plus deep-link — still supported.
    const legacyCheckout = url.searchParams.get("checkout");
    // Legacy sessionStorage intent from an earlier CTA that predated the cart.
    const legacyIntent = readIntent();

    // Clean checkout param from URL immediately.
    if (legacyCheckout || url.searchParams.get("resume")) {
      url.searchParams.delete("checkout");
      url.searchParams.delete("resume");
      window.history.replaceState({}, "", url.pathname + (url.search || "") + url.hash);
    }

    if (!isLoggedIn) return;

    // Legacy: turn a single-plan ?checkout= param into a cart addition + checkout.
    if (legacyCheckout && (legacyCheckout === "basis" || legacyCheckout === "plus")) {
      resumedRef.current = true;
      const planMeta = plans.find((p) => p.key === legacyCheckout);
      if (planMeta) {
        cart.add({
          type: "subscribe", plan: planMeta.key, label: planMeta.label,
          short_label: planMeta.short_label, price_eur: planMeta.price_eur, billing: "monthly",
        });
      }
      sessionStorage.setItem(RESUME_KEY, "1");
      setCartOpen(true);
      // Kick off next tick so state settles.
      setTimeout(() => checkoutNextItem(), 60);
      return;
    }

    if (legacyIntent && (legacyIntent.plan || legacyIntent.domain)) {
      resumedRef.current = true;
      clearIntent();
      if (legacyIntent.type === "subscribe" && legacyIntent.plan) {
        const planMeta = plans.find((p) => p.key === legacyIntent.plan);
        cart.add({
          type: "subscribe",
          plan: legacyIntent.plan,
          label: planMeta?.label || `Zynthoro Site ${legacyIntent.plan}`,
          short_label: planMeta?.short_label || legacyIntent.plan,
          price_eur: planMeta?.price_eur || 0,
          billing: "monthly",
        });
      } else if (legacyIntent.type === "domain" && legacyIntent.domain && legacyIntent.ext) {
        cart.add({
          type: "domain", domain: legacyIntent.domain, ext: legacyIntent.ext,
          label: legacyIntent.domain, price_eur: legacyIntent.price_eur || 0, billing: "yearly",
        });
      }
      sessionStorage.setItem(RESUME_KEY, "1");
      setCartOpen(true);
      setTimeout(() => checkoutNextItem(), 60);
      return;
    }

    if (wantResume) {
      resumedRef.current = true;
      if (cart.items.length === 0) {
        // Nothing to resume — clear the flag so we don't loop next visit.
        sessionStorage.removeItem(RESUME_KEY);
        return;
      }
      // ALWAYS open the drawer first so the user sees their items even
      // if the checkout redirect stalls or fails.
      setCartOpen(true);
      // Kick off Stripe on the next tick so React commits the drawer
      // open state before we hit `window.location.href = ...`.
      setTimeout(() => checkoutNextItem(), 80);
    }
  }, [authLoading, isLoggedIn, plans]);

  return (
    <>
      <Navbar />
      <main data-testid="page-website-domains" className="bg-white">
        {/* TOP — Domain search (single, canonical section) */}
        <section id="domains" className="py-16 sm:py-20 bg-[#fafbff] border-y border-[#eef1f7] scroll-mt-24" data-testid="wd-domains">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-10">
              <p className="zy-eyebrow mb-3">Custom domain</p>
              <h2 className="text-[28px] sm:text-[32px] font-bold tracking-tight text-[#0A1628]">
                <Globe size={22} className="inline -mt-1 mr-2 text-[#1A4FFF]" />
                Search &amp; register a domain
              </h2>
              <p className="mt-4 text-[14.5px] leading-relaxed text-[#4a5568]">
                {extensions.length > 11 ? "Worldwide domain search" : "Domain search"}
                , sorted by price. One-year registration.
                DNS instructions delivered by email after payment.
              </p>
            </div>

            {/* Search form */}
            <form onSubmit={searchDomains} className="max-w-xl mx-auto mb-8" data-testid="wd-search-form">
              <div className="flex gap-2 flex-col sm:flex-row">
                <div className="flex-1 relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
                  <input
                    type="text"
                    value={query}
                    onChange={(ev) => setQuery(ev.target.value)}
                    placeholder="mycoolbrand"
                    className="w-full pl-9 pr-3 py-3 border border-[#dde3ed] rounded-lg text-[15px] bg-white focus:outline-none focus:border-[#1A4FFF] focus:ring-2 focus:ring-[#1A4FFF]/15 transition"
                    data-testid="wd-search-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={searching || !query.trim()}
                  data-testid="wd-search-submit"
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg px-6 py-3 text-[14px] font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-95"
                  style={{ background: "#1A4FFF" }}
                >
                  {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                  Search
                </button>
              </div>
            </form>

            {/* Popular TLD shortcut rail — always exactly these 7 populars, price shown only when known. */}
            <div className="mb-8 flex flex-wrap justify-center gap-1.5" data-testid="wd-extensions">
              {POPULAR_TLDS.map((ext) => {
                const known = extensions.find((e) => e.ext === ext);
                return (
                  <span
                    key={ext}
                    data-testid={`wd-extension-${ext}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-semibold bg-white border border-[#e0e6f0] text-[#333]"
                  >
                    .{ext}
                    {known ? (
                      <span className="text-[#666] font-normal">€{Number(known.price_eur).toFixed(0)}</span>
                    ) : null}
                  </span>
                );
              })}
            </div>

            {/* Results */}
            {results.length > 0 && (
              <div className="max-w-xl mx-auto space-y-2" data-testid="wd-results">
                {results.map((r) => {
                  // status: "available" | "unavailable" | "unknown" | "yours" | "taken"
                  // "yours" and "taken" come from real-time internal
                  // cross-ref against website_domain_orders — they take
                  // precedence over any Namecheap answer.
                  const status =
                    r.status || (r.available ? "available" : "unavailable");
                  const isAvail = status === "available";
                  const isUnknown = status === "unknown";
                  const isYours = status === "yours";
                  const isInternalTaken = status === "taken";
                  const purchasable = r.purchasable !== false && isAvail;
                  const IconComp = isAvail
                    ? CheckCircle2
                    : isYours
                      ? CheckCircle2
                      : XCircle;
                  const iconTone = isAvail
                    ? "text-[#059669]"
                    : isYours
                      ? "text-[#059669]"
                      : isUnknown
                        ? "text-[#B7791F]"
                        : "text-[#999]";
                  const rowBg = isAvail
                    ? "border-[#eef1f7] bg-white"
                    : isYours
                      ? "border-emerald-200 bg-emerald-50/60"
                      : isUnknown
                        ? "border-[#f0dcb0] bg-[#fffbea]"
                        : "border-[#eef1f7] bg-[#fafafa] opacity-70";
                  const line2 = isAvail
                    ? `Available · €${Number(r.price_eur).toFixed(0)} / year`
                    : isYours
                      ? "Yours — already registered on your account"
                      : isInternalTaken
                        ? "Taken — registered on Zynthoro"
                        : isUnknown
                          ? "Availability check failed. Try again."
                          : `Taken · €${Number(r.price_eur).toFixed(0)} / year`;
                  return (
                    <div
                      key={r.domain}
                      data-testid={`wd-result-${r.ext}`}
                      data-status={status}
                      className={`flex items-center gap-3 p-4 rounded-xl border ${rowBg}`}
                    >
                      <IconComp size={18} className={`${iconTone} shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[15px] text-[#0A1628]">{r.domain}</p>
                        <p className="text-[12px] text-[#666]">{line2}</p>
                      </div>
                      {isYours && (
                        <span
                          className="inline-flex items-center px-2.5 py-1 rounded-full border border-emerald-200 bg-white text-emerald-700 text-[11.5px] font-semibold"
                          data-testid={`wd-yours-${r.ext}`}
                        >
                          ✓ Yours
                        </span>
                      )}
                      {purchasable && (
                        <button
                          type="button"
                          onClick={() => addDomainToCart(r)}
                          data-testid={`wd-order-${r.ext}`}
                          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-95"
                          style={{ background: "#1A4FFF" }}
                        >
                          <ShoppingCart size={13} /> Add to cart
                        </button>
                      )}
                    </div>
                  );
                })}
                {results.length < total && (
                  <div className="pt-2 text-center">
                    <button
                      type="button"
                      onClick={loadMoreDomains}
                      disabled={loadingMore}
                      data-testid="wd-show-more"
                      className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-[13px] font-semibold border border-[#e0e6f0] bg-white text-[#0A1628] hover:bg-[#f6f8fc] transition-colors disabled:opacity-60"
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 size={14} className="animate-spin" /> Loading…
                        </>
                      ) : (
                        <>
                          Show more <span className="text-[#666] font-normal">({results.length}/{total})</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* BOTTOM — Site plans */}
        <section className="pb-24 pt-16" data-testid="wd-plans">
          <div className="max-w-4xl mx-auto px-6">
            <div className="max-w-2xl mx-auto text-center mb-12">
              <p className="zy-eyebrow mb-3">Site plans</p>
              <h2 className="text-[28px] sm:text-[32px] font-bold tracking-tight text-[#0A1628]">
                Build your website. Grow your business.
              </h2>
              <p className="mt-4 text-[14.5px] leading-relaxed text-[#4a5568]">
                Choose from 2 simple plans with hosting, SSL and 100 templates included. Connect or move your domain when you’re ready.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {plans.map((t) => {
                const isPopular = t.popular;
                return (
                  <div
                    key={t.key}
                    data-testid={`wd-plan-${t.key}`}
                    className="relative bg-white border rounded-2xl p-7 flex flex-col transition-shadow"
                    style={{
                      borderColor: isPopular ? "rgba(26,79,255,0.35)" : "#eef1f7",
                      boxShadow: isPopular ? "0 12px 40px -20px rgba(26,79,255,0.35)" : "0 2px 8px -4px rgba(15,23,42,0.05)",
                    }}
                  >
                    {isPopular && (
                      <span className="absolute -top-3 left-6 px-3 py-1 rounded-full text-[10.5px] font-semibold uppercase tracking-wider text-white" style={{ background: "#1A4FFF" }}>
                        Most popular
                      </span>
                    )}
                    <p className="text-[12.5px] font-semibold uppercase tracking-wider text-[#666]">{t.short_label}</p>
                    <p className="mt-1 text-[13.5px] text-[#0A1628]">{t.label}</p>
                    <div className="mt-4 flex items-baseline gap-2">
                      <span className="text-[34px] font-bold tracking-tight" data-testid={`wd-plan-price-${t.key}`}>
                        €{Number(t.price_eur).toFixed(0)}
                      </span>
                      <span className="text-[13px] text-[#666]">/month</span>
                    </div>
                    <p className="text-[13px] text-[#666] mt-1">{t.tagline}</p>

                    <ul className="mt-6 space-y-2.5 flex-1">
                      {(t.features || []).map((f, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-[13.5px] text-[#333]">
                          <CheckCircle2 size={14} className="mt-1 shrink-0 text-[#1A4FFF]" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      type="button"
                      onClick={() => addPlanToCart(t)}
                      data-testid={`wd-plan-cta-${t.key}`}
                      className="mt-8 w-full inline-flex items-center justify-center gap-1.5 rounded-full px-5 py-2.5 text-[13.5px] font-semibold text-white transition-opacity hover:opacity-90"
                      style={{ background: "#1A4FFF" }}
                    >
                      <ShoppingCart size={13} /> Add {t.short_label} to cart
                    </button>
                  </div>
                );
              })}
            </div>

            <p className="mt-10 text-center text-[12px] text-[#888]">
              Secured by Stripe. All prices in EUR. Cancel anytime from your Stripe billing portal.
            </p>
          </div>
        </section>
      </main>

      {/* Cart FAB (bottom-right, always visible when items > 0) */}
      {cart.items.length > 0 && !cartOpen && (
        <button
          type="button"
          onClick={() => setCartOpen(true)}
          data-testid="wd-cart-fab"
          className="fixed bottom-6 left-6 sm:left-auto sm:right-6 sm:bottom-28 z-[60] inline-flex items-center gap-2 px-5 py-3 rounded-full text-white shadow-lg hover:opacity-95"
          style={{ background: "#1A4FFF", boxShadow: "0 14px 40px -12px rgba(26,79,255,0.55)" }}
        >
          <ShoppingCart size={16} />
          <span className="text-[13.5px] font-semibold">Cart</span>
          <span
            data-testid="wd-cart-count"
            className="inline-flex items-center justify-center min-w-[22px] h-[22px] rounded-full text-[11px] font-bold text-[#1A4FFF] bg-white px-1.5"
          >
            {cart.items.length}
          </span>
        </button>
      )}

      {/* Cart drawer */}
      {cartOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-[70]"
            onClick={() => setCartOpen(false)}
            data-testid="wd-cart-backdrop"
          />
          <aside
            data-testid="wd-cart-drawer"
            className="fixed top-0 right-0 h-full w-full sm:w-[520px] lg:w-[560px] bg-white z-[80] shadow-2xl flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#eee]">
              <div className="flex items-center gap-2.5">
                <ShoppingCart size={22} className="text-[#1A4FFF]" />
                <h3 className="text-[19px] font-semibold text-[#0A1628]">Your cart</h3>
                <span className="text-[13.5px] text-[#888]">({cart.items.length})</span>
              </div>
              <button
                type="button"
                onClick={() => setCartOpen(false)}
                data-testid="wd-cart-close"
                className="p-2 rounded-full hover:bg-[#f4f6fb]"
                aria-label="Close cart"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3.5" data-testid="wd-cart-items">
              {cart.items.length === 0 ? (
                <p className="text-[15px] text-[#666] leading-relaxed">
                  Your cart is empty. Pick a Site plan or register a domain to get started.
                </p>
              ) : (
                cart.items.map((it) => (
                  <div
                    key={
                      it.type === "subscribe" ? `plan:${it.plan}`
                      : it.type === "kickstart" ? `kickstart:${it.plan}`
                      : `domain:${it.domain}`
                    }
                    data-testid={`wd-cart-item-${
                      it.type === "subscribe" ? it.plan
                      : it.type === "kickstart" ? `kickstart-${it.plan}`
                      : it.ext
                    }`}
                    className="flex items-start gap-3 p-4 rounded-xl border border-[#e6e9f0] bg-white"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[15.5px] font-semibold text-[#0A1628] leading-snug">{it.label}</p>
                      <p className="text-[13.5px] text-[#666] mt-1.5">
                        €{Number(it.price_eur).toFixed(2)}{" "}
                        · {it.billing === "monthly" ? "per month"
                          : it.billing === "onetime" ? "one-time · lifetime"
                          : "per year"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => cart.remove(it)}
                      data-testid={`wd-cart-remove-${
                        it.type === "subscribe" ? it.plan
                        : it.type === "kickstart" ? `kickstart-${it.plan}`
                        : it.ext
                      }`}
                      className="p-2 rounded-lg hover:bg-[#fdecec] text-[#c53030]"
                      aria-label="Remove"
                    >
                      <Trash2 size={17} />
                    </button>
                  </div>
                ))
              )}

              {/* Exclusive checkout deal — 50% off Kickstart, only if the
                  cart contains a Site plan (Basis/Plus) or a Domain. */}
              {cart.items.some((i) => i.type === "subscribe" || i.type === "domain") && (
                <div
                  data-testid="wd-kickstart-deal"
                  className="mt-5 p-5 rounded-2xl border border-[#D4AF37]/40 bg-[#FFFBEA]"
                >
                  <p className="text-[12.5px] font-bold uppercase tracking-[0.14em] text-[#8A6B00]">
                    Exclusive checkout deal – 50% off
                  </p>
                  <p className="text-[14px] text-[#0A1628]/85 mt-2.5 leading-relaxed">
                    Only visible because you added a website or domain.
                    <br />
                    You get 4 AI specialists powered by Claude &amp; Gemini.
                    <br />
                    One-time payment. Lifetime access. No monthly fees.
                  </p>

                  <div className="mt-4 space-y-3">
                    {KICKSTART_DEALS.map((deal) => {
                      const alreadyInCart = cart.items.some(
                        (i) => i.type === "kickstart" && i.plan === deal.key,
                      );
                      return (
                        <div
                          key={deal.key}
                          data-testid={`wd-kickstart-${deal.key}`}
                          className="p-4 rounded-xl bg-white border border-[#eee]"
                        >
                          <div className="flex items-baseline justify-between gap-2">
                            <p className="text-[15.5px] font-semibold text-[#0A1628]">{deal.label}</p>
                            <p className="text-[15px] font-semibold text-[#0A1628] whitespace-nowrap">
                              <span className="line-through text-[#999] font-normal mr-2 text-[13.5px]">
                                €{deal.original_eur}
                              </span>
                              €{deal.price_eur}
                            </p>
                          </div>
                          <ul className="mt-2 space-y-1">
                            {deal.features.map((f, i) => (
                              <li key={i} className="text-[13px] text-[#555] leading-relaxed">· {f}</li>
                            ))}
                          </ul>
                          <button
                            type="button"
                            data-testid={`wd-kickstart-add-${deal.key}`}
                            disabled={alreadyInCart}
                            onClick={() => addKickstartToCart(deal)}
                            className="mt-3.5 w-full inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ background: "#D4AF37" }}
                          >
                            {alreadyInCart ? "Added" : "Add to cart"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-[#eee] px-6 py-5 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[14.5px] text-[#555]">Subtotal</span>
                <span data-testid="wd-cart-subtotal" className="text-[20px] font-bold text-[#0A1628]">
                  €{Number(cart.total).toFixed(2)}
                </span>
              </div>
              <p className="text-[12.5px] text-[#888] leading-relaxed">
                Subscriptions billed monthly, domains billed yearly. Taxes calculated by Stripe.
              </p>

              {!isLoggedIn && cart.items.length > 0 && (
                <div
                  data-testid="wd-cart-auth-required"
                  className="rounded-lg border border-[#1A4FFF]/25 bg-[#1A4FFF]/[0.06] p-3.5 text-[13.5px] text-[#0A1628] flex items-start gap-2"
                >
                  <LogIn size={15} className="mt-0.5 shrink-0 text-[#1A4FFF]" />
                  <span>
                    Log in or create an account to check out — your cart will be waiting for you.
                  </span>
                </div>
              )}

              <button
                type="button"
                onClick={startCheckout}
                disabled={cart.items.length === 0 || checkingOut}
                data-testid="wd-cart-checkout"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[15.5px] font-semibold text-white transition-opacity hover:opacity-95 disabled:opacity-50 shadow-sm"
                style={{ background: "#1A4FFF" }}
              >
                {checkingOut ? <Loader2 size={16} className="animate-spin" /> : null}
                {!isLoggedIn ? "Log in to checkout" : (checkingOut ? "Redirecting…" : "Checkout")}
                {!checkingOut && <ArrowRight size={16} />}
              </button>

              {!isLoggedIn && cart.items.length > 0 && (
                <p className="text-center text-[13px] text-[#666]">
                  Don&apos;t have an account?{" "}
                  <a href="/signup?return=/website?resume=1" className="text-[#1A4FFF] font-semibold">Sign up</a>
                </p>
              )}
            </div>
          </aside>
        </>
      )}

      <Footer />
    </>
  );
}
